/**
 * SF-4762 — one platform flag: switch, exempt list, latest change + history (SF-4811).
 *
 * Both directions of the switch ask first: a platform flag changes the whole
 * platform at once. While a save runs the switch shows where it is heading.
 *
 * Save outcomes are only claimed when known. A successful PUT returns the
 * stored flag, which shows at once (a failed reload afterwards never snaps the
 * switch back). A refusal (400/401/403/404) wrote nothing. A lost race (409)
 * or an unknown outcome (no answer, timeout, 5xx) may have changed the flag,
 * so the card reloads it and says so instead of "nothing was changed". Until
 * that reload ends the switch and the exempt list stay locked: a second click
 * in that gap could write the flag twice.
 */
import { useState } from 'react';
import { Card, Space, Typography, message } from 'antd';
import type { AxiosError } from 'axios';
import { FlagSwitch } from '../companies/components/FlagSwitch';
import {
  platformFlagUpdateMutationArgs,
  usePlatformFlagHistory,
  useUpdatePlatformFlag,
  type PlatformFlag,
  type PlatformFlagUpdate,
} from './api/platformFlagsClient';
import { ExemptEmailsEditor } from './ExemptEmailsEditor';
import { FlagHistory } from './FlagHistory';
import { listWording } from './format';

const { Text, Title } = Typography;

export const UNKNOWN_OUTCOME_TEXT =
  'Could not confirm the save. Reload to check the current setting.';
export const FLAG_CHANGED_TEXT = 'Someone else changed this flag. Reload and try again.';
export const SAVED_NOT_REFRESHED_TEXT =
  'Saved, but could not refresh the page. Reload to see the latest.';

/** Statuses sf-api answers before writing anything, so nothing changed. */
const REFUSED_STATUSES = [400, 401, 403, 404];

/** What to tell the user about a failed PUT, and whether to reload the flag. */
function describeFailure(error: unknown, label: string): { text: string; reload: boolean } {
  const response = (error as AxiosError<{ code?: string; message?: string }> | undefined)?.response;
  const status = response?.status;
  if (status === 409) return { text: FLAG_CHANGED_TEXT, reload: true };
  if (status === 403) {
    return { text: 'You do not have permission to change platform feature flags.', reload: false };
  }
  if (status !== undefined && REFUSED_STATUSES.includes(status)) {
    return {
      text: response?.data?.message || `Could not save "${label}": the change was refused.`,
      reload: false,
    };
  }
  // No answer, a timeout or a 5xx (e.g. 503 FLAG_STORE_UNAVAILABLE): the
  // write may have happened, so never claim that nothing changed.
  return { text: UNKNOWN_OUTCOME_TEXT, reload: true };
}

/** The settled result of a query refetch (TanStack resolves, it does not throw). */
type RefetchResult = { isError: boolean };

export interface FlagCardProps {
  flag: PlatformFlag;
  canWrite: boolean;
  /** Why the controls are locked, when they are. */
  readOnlyReason: string | null;
  /** Reload the flag list after a save. */
  refetchFlags: () => Promise<RefetchResult>;
}

export const FlagCard: React.FC<FlagCardProps> = ({
  flag,
  canWrite,
  readOnlyReason,
  refetchFlags,
}) => {
  const update = useUpdatePlatformFlag();
  const history = usePlatformFlagHistory(flag.key);
  const [pending, setPending] = useState<PlatformFlagUpdate | null>(null);
  // A save whose outcome is not known, while the reload that settles it runs.
  const [confirming, setConfirming] = useState<PlatformFlagUpdate | null>(null);
  // The flag the last successful PUT returned; shown until the list brings a
  // newer copy of this flag (a new `flag` object), so a failed reload keeps it.
  const [saved, setSaved] = useState<PlatformFlag | null>(null);
  const [savedFrom, setSavedFrom] = useState(flag);
  if (savedFrom !== flag) {
    setSavedFrom(flag);
    setSaved(null);
  }
  const shown = saved ?? flag;

  const reload = async (): Promise<boolean> => {
    const results = await Promise.all([refetchFlags(), history.refetch()]);
    return results.every((r) => !r.isError);
  };

  const save = async (body: PlatformFlagUpdate, successText: string): Promise<boolean> => {
    setPending(body);
    let stored: PlatformFlag | undefined;
    try {
      stored = (await update.mutateAsync(platformFlagUpdateMutationArgs(flag.key, body))).data;
    } catch (error) {
      const failure = describeFailure(error, flag.label);
      if (failure.reload) setConfirming(body);
      setPending(null);
      message.error(failure.text);
      if (failure.reload) {
        try {
          await reload();
        } finally {
          setConfirming(null);
        }
      }
      return false;
    }
    if (stored) setSaved(stored);
    setPending(null);
    if (await reload()) message.success(successText);
    else message.warning(SAVED_NOT_REFRESHED_TEXT);
    return true;
  };

  const wording = listWording(flag);
  const pendingEnabled = typeof pending?.enabled === 'boolean' ? pending.enabled : null;
  const busy = pending !== null || confirming !== null;
  const savingList = pending?.exemptEmails !== undefined || confirming?.exemptEmails !== undefined;

  return (
    <Card data-testid={`platform-flag-${flag.key}`}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space align="start">
          <FlagSwitch
            testId={`platform-flag-${flag.key}-toggle`}
            ariaLabel={flag.label}
            checked={shown.enabled}
            pendingValue={pendingEnabled}
            loading={confirming !== null}
            disabled={!canWrite || busy}
            disabledReason={readOnlyReason}
            danger
            confirmOn={{
              title: `Switch "${flag.label}" on?`,
              description: 'This takes effect at once for the whole platform.',
              okText: 'Switch on',
            }}
            confirmOff={{
              title: `Switch "${flag.label}" off?`,
              description: 'This takes effect at once for the whole platform.',
              okText: 'Switch off',
            }}
            onChange={(next) =>
              void save({ enabled: next }, `${flag.label} switched ${next ? 'on' : 'off'}`)
            }
          />
          <div>
            <Title level={5} style={{ margin: 0 }}>
              {flag.label}
            </Title>
            <Text type="secondary">{flag.description}</Text>
            {flag.companyField && (
              <div>
                <Text type="secondary">Also has a switch per company.</Text>
              </div>
            )}
          </div>
        </Space>

        {flag.exemptAccounts && (
          <ExemptEmailsEditor
            saved={shown.exemptEmails}
            canWrite={canWrite}
            saving={savingList}
            busy={busy}
            wording={wording}
            onSave={(next) => save({ exemptEmails: next }, wording.savedText)}
          />
        )}

        <div>
          <div>
            <Text strong>Latest change</Text>
          </div>
          <FlagHistory
            flagKey={flag.key}
            flagLabel={flag.label}
            rows={history.data?.data}
            isLoading={history.isLoading}
            isError={history.isError}
            listName={wording.historyPrefix}
          />
        </div>
      </Space>
    </Card>
  );
};
